/**
 * WebSocket Service - Manages persistent WebSocket connection
 * 
 * MIGRATION NOTE: AI streaming has been migrated to Lambda Function URL
 * (see streaming.ts - functionUrlAIRequest) which supports 15-minute operations.
 * 
 * This WebSocket service is now primarily used for:
 * - Real-time notifications (title updates, plan progress)
 * - Backward compatibility fallback for AI streaming
 * 
 * Primary AI streaming: Lambda Function URL (15-min timeout)
 * Fallback AI streaming: WebSocket (29s API Gateway timeout)
 */

import { StreamEvent, StreamHandlers } from './streaming';
import type { DocumentOperation } from '../types/document-operations';
import { logger } from '../utils/logger';

interface QueuedMessage {
  body: Record<string, unknown>;
  handlers: StreamHandlers;
  requestId: string;
  resolve: () => void;
  reject: (error: Error) => void;
}

// Buffer for accumulating chunked messages
interface ChunkBuffer {
  id: string;
  total: number;
  chunks: string[];
  receivedCount: number;
  type: 'text_delta' | 'content' | 'document_operation';
  metadata?: { jobId?: string; requestId?: string };
}

class WebSocketService {
  private socket: WebSocket | null = null;
  private connectionPromise: Promise<void> | null = null;
  private messageHandlers = new Map<string, StreamHandlers>();
  private jobHandlers = new Map<string, StreamHandlers>();  // Job-specific handlers for worker delegation
  private idleTimeout: NodeJS.Timeout | null = null;
  private reconnectTimeout: NodeJS.Timeout | null = null;
  private wsUrl: string | null = null;
  private token: string | null = null;
  private reconnectAttempts = 0;
  private shouldReconnect = true;
  private connectionListeners = new Set<(connected: boolean) => void>();
  private messageQueue: QueuedMessage[] = [];
  private currentRequestId: string | null = null;  // Track active request
  private connectionId: string | null = null;  // WebSocket connection ID from server
  private keepaliveInterval: ReturnType<typeof setInterval> | null = null;
  
  // Chunk buffers for reassembling large messages (keyed by chunk id)
  private chunkBuffers = new Map<string, ChunkBuffer>();
  
  private readonly IDLE_TIMEOUT_MS = 15 * 60 * 1000; // 15 minutes - matches Lambda timeout for worker delegation
  private readonly KEEPALIVE_INTERVAL_MS = 4 * 60 * 1000; // 4 minutes - keep connection alive (API Gateway idle is typically 10 min)
  private readonly MAX_RECONNECT_ATTEMPTS = 5;
  private readonly RECONNECT_BASE_DELAY_MS = 1000; // Base delay for exponential backoff
  private readonly RECONNECT_MAX_DELAY_MS = 30000; // Cap at 30 seconds
  
  /**
   * Connect to WebSocket endpoint
   * @param wsUrl WebSocket URL (e.g., wss://...)
   * @param token Authentication token
   */
  async connect(wsUrl: string, token: string): Promise<void> {
    // Already connected
    if (this.socket?.readyState === WebSocket.OPEN) {
      this.resetIdleTimeout();
      return;
    }
    
    // Connection in progress
    if (this.connectionPromise) {
      return this.connectionPromise;
    }
    
    // Store credentials for reconnection (persist across cleanup cycles)
    this.wsUrl = wsUrl;
    this.token = token;
    this.shouldReconnect = true;
    
    // Clear stale socket before creating new one
    if (this.socket) {
      try { this.socket.close(); } catch (_e) { /* ignore */ }
      this.socket = null;
    }
    
    this.connectionPromise = new Promise((resolve, reject) => {
      // Pass auth token as query parameter ($connect validates it server-side)
      const wsUrlWithToken = `${wsUrl}?token=${encodeURIComponent(token)}`;
      this.socket = new WebSocket(wsUrlWithToken);
      
      this.socket.onopen = () => {
        logger.debug('[WebSocketService] Connected');
        this.reconnectAttempts = 0;
        this.resetIdleTimeout();
        this.startKeepalive(); // Start sending periodic pings
        this.notifyConnectionListeners(true);
        
        // Request connectionId from server (needed for worker delegation)
        try {
          this.socket!.send(JSON.stringify({ action: 'get_connection_id' }));
          logger.debug('[WebSocketService] Requested connectionId');
        } catch (error) {
          logger.warn('[WebSocketService] Failed to request connectionId:', error);
        }
        
        // Process queued messages
        this.processMessageQueue();
        
        // Re-register any active jobs with the new connectionId
        // (happens after connectionId is received in onmessage handler)
        
        resolve();
      };
      
      this.socket.onerror = (error) => {
        logger.error('[WebSocketService] Connection error:', error);
        // Clear connectionPromise so future connect() calls can retry
        this.connectionPromise = null;
        reject(new Error('WebSocket connection failed'));
      };
      
      this.socket.onclose = (event) => {
        logger.debug('[WebSocketService] Closed', event.code, event.reason);
        // Clear connectionPromise so reconnect/retry can create a new one
        this.connectionPromise = null;
        this.notifyConnectionListeners(false);
        this.stopKeepalive();
        
        // Handle reconnection if needed
        if (this.shouldReconnect && this.reconnectAttempts < this.MAX_RECONNECT_ATTEMPTS) {
          this.scheduleReconnect();
        } else {
          // Exhausted reconnect attempts — clean up handlers but PRESERVE wsUrl/token
          // so that the React layer can trigger a fresh connection cycle
          this.cleanupHandlers();
          
          if (!this.shouldReconnect) {
            // Intentional close (idle timeout, manual disconnect) — full cleanup
            this.cleanupFull();
          }
        }
      };
      
      this.socket.onmessage = (event) => {
        this.resetIdleTimeout();
        
        try {
          const data: StreamEvent & { requestId?: string; connectionId?: string } = JSON.parse(event.data);
          
          // Capture connectionId if sent by server (e.g., connection_id response or pong)
          if (data.connectionId) {
            const previousConnectionId = this.connectionId;
            this.connectionId = data.connectionId;
            logger.debug('[WebSocketService] Connection ID received:', this.connectionId);
            
            // If we reconnected (previous connectionId exists and is different),
            // notify the server about active jobs so it can update their connectionId
            if (previousConnectionId && previousConnectionId !== this.connectionId) {
              const activeJobIds = this.getActiveJobIds();
              if (activeJobIds.length > 0) {
                logger.debug(`[WebSocketService] Reconnected with new connectionId — re-registering ${activeJobIds.length} active jobs`);
                try {
                  this.socket!.send(JSON.stringify({
                    action: 'resume_jobs',
                    jobIds: activeJobIds,
                    previousConnectionId,
                  }));
                } catch (error) {
                  logger.warn('[WebSocketService] Failed to send resume_jobs:', error);
                }
              }
            }
          }
          
          // Handle connection_id response - just capture the ID, no handler needed
          if (data.type === 'connection_id' || data.type === 'pong') {
            // connectionId already captured above
            return;
          }
          
          // Handle title_update globally (not tied to specific request)
          if (data.type === 'title_update' && data.conversationId && data.title) {
            logger.debug('[WebSocketService] Title update received:', { conversationId: data.conversationId, title: data.title });
            // Broadcast to all handlers (title updates affect conversation list)
            let handlerCalled = false;
            this.messageHandlers.forEach(handler => {
              if (handler.onTitleUpdate) {
                handler.onTitleUpdate(data.conversationId, data.title);
                handlerCalled = true;
              }
            });
            // Also broadcast to job handlers
            this.jobHandlers.forEach(handler => {
              if (handler.onTitleUpdate) {
                handler.onTitleUpdate(data.conversationId!, data.title!);
                handlerCalled = true;
              }
            });
            logger.debug('[WebSocketService] Title update broadcasted to', this.messageHandlers.size + this.jobHandlers.size, 'handlers, called:', handlerCalled);
            return;
          }
          
          // Micro-unit events use jobId = Step Functions execution ARN, which
          // the frontend doesn't know at registration time. Broadcast them to
          // all job handlers (same pattern as title_update). Each handler
          // filters by reqId so only the relevant coding project responds.
          if (data.type === 'micro_unit_progress' || data.type === 'micro_unit_complete' || data.type === 'micro_unit_paused') {
            let handled = false;
            this.jobHandlers.forEach(handler => {
              switch (data.type) {
                case 'micro_unit_progress':
                  handler.onMicroUnitProgress?.(data);
                  handled = true;
                  break;
                case 'micro_unit_complete':
                  handler.onMicroUnitComplete?.(data);
                  handled = true;
                  break;
                case 'micro_unit_paused':
                  handler.onMicroUnitPaused?.(data);
                  handled = true;
                  break;
              }
            });
            if (handled) {
              logger.debug(`[WebSocketService] ${data.type} broadcasted to ${this.jobHandlers.size} job handler(s)`);
            } else {
              logger.warn(`[WebSocketService] ${data.type} received but no job handlers registered`);
            }
            return;
          }

          // Route by jobId first (for worker delegation), then by requestId
          let handlers: StreamHandlers | null = null;
          
          if (data.jobId && this.jobHandlers.has(data.jobId)) {
            handlers = this.jobHandlers.get(data.jobId) || null;
          } else if (data.requestId) {
            handlers = this.messageHandlers.get(data.requestId) || null;
          }
          
          // Handle timeout messages from API Gateway
          if ('message' in data && data.message === 'Endpoint request timed out') {
            if (handlers) {
              logger.error('[WebSocketService] Lambda timeout detected');
              handlers.onError?.('Request timed out - the operation took too long. Try breaking it into smaller steps.');
              if (data.requestId) {
                this.messageHandlers.delete(data.requestId);
              }
            } else {
              // Timeout arrived after handler was cleaned up (request completed successfully)
              logger.debug('[WebSocketService] Timeout notification received after request completed (ignoring)');
            }
            return;
          }
          
          if (!handlers) {
            logger.warn('[WebSocketService] No handler for message:', data);
            return;
          }
          
          // Route events to handlers
          switch (data.type) {
            case 'keepalive':
              // Ignore keep-alive pings, they just prevent timeout
              logger.debug('[WebSocketService] Keep-alive received');
              break;
            case 'plan_ack':
              handlers.onPlanAck?.(data.jobId || '', data.message || '');
              break;
            case 'plan_progress':
              handlers.onPlanProgress?.(data.message || '', data.currentStep);
              break;
            case 'plan_step_start':
              handlers.onPlanStepStart?.(data.step || 0, data.total || 0, data.description || '');
              break;
            case 'plan_step_complete':
              handlers.onPlanStepComplete?.(data.step || 0);
              break;
            case 'execution_done':
            case 'plan_done':  // backward compat
              handlers.onPlanDone?.(data.message || '', data.edits);  // Include edits from Worker
              // Clean up handler after plan completes
              if (data.requestId) {
                setTimeout(() => {
                  this.messageHandlers.delete(data.requestId);
                }, 5000);
              }
              break;
            case 'plan_error':
              handlers.onPlanError?.(data.message || 'Plan execution failed');
              // Clean up handler on error
              if (data.requestId) {
                this.messageHandlers.delete(data.requestId);
              }
              break;
            case 'plan_cancelled':
              handlers.onPlanCancelled?.(data.message || 'Plan was cancelled');
              // Clean up handler on cancellation
              if (data.requestId) {
                this.messageHandlers.delete(data.requestId);
              }
              break;
            case 'init':
              handlers.onInit?.(data);
              break;
            case 'progress':
              handlers.onProgress?.(data.message || '');
              break;
            case 'tool_start':
              handlers.onToolStart?.(data.tool || '', data.query);
              break;
            case 'tool_complete':
              handlers.onToolComplete?.(data.tool || '');
              break;
            case 'content':
              // Handle chunked content messages
              if (data._chunked) {
                const { id, index, total, isLast } = data._chunked;
                
                // Initialize buffer if first chunk
                if (!this.chunkBuffers.has(id)) {
                  this.chunkBuffers.set(id, {
                    id,
                    total,
                    chunks: new Array(total).fill(''),
                    receivedCount: 0,
                    type: 'content',
                  });
                }
                
                const buffer = this.chunkBuffers.get(id)!;
                buffer.chunks[index] = data.delta || '';
                buffer.receivedCount++;
                
                // Stream each chunk as it arrives for responsiveness
                handlers.onContent?.(data.delta || '');
                
                // Clean up buffer when complete
                if (isLast || buffer.receivedCount === total) {
                  this.chunkBuffers.delete(id);
                  logger.debug(`[WebSocketService] Completed chunked content: ${total} chunks`);
                }
              } else {
                logger.debug('[WebSocketService] Content event:', data.delta?.substring(0, 50));
                handlers.onContent?.(data.delta || '');
              }
              break;
            case 'warning':
              handlers.onWarning?.(data.message || '');
              break;
            case 'error':
              handlers.onError?.(data.message || 'Unknown error');
              // Clean up this message's handler immediately on error
              if (data.requestId) {
                this.messageHandlers.delete(data.requestId);
              }
              break;
            case 'handoff':
              handlers.onHandoff?.(data.message || 'Moving to background processing...');
              // Don't clean up - worker will continue sending events to same requestId
              break;
            case 'edit_preview':
              handlers.onEditPreview?.(
                data.explanation || 'Making edits...', 
                data.operationCount, 
                data.editType
              );
              break;
            case 'document_operation':
              // Stream individual operations to document editor
              handlers.onDocumentOperation?.(data.operation, data.partial);
              
              // Also forward to global document operation handler for inline editing
              if (typeof window.__handleDocumentOperation === 'function') {
                window.__handleDocumentOperation(data.operation);
              }
              break;
            case 'document_operation_complete':
              // All operations have been sent
              handlers.onDocumentOperationComplete?.(data.operationCount);
              logger.debug('[WebSocketService] All document operations sent:', data.operationCount);
              break;
            case 'done':
              handlers.onDone?.(data.message || '', data.edits, data.tokenUsage, data.truncated, data.stop_reason, data.resolvedModel);
              // Delay cleanup to allow for other cleanup
              if (data.requestId) {
                setTimeout(() => {
                  this.messageHandlers.delete(data.requestId);
                }, 5000); // 5 second grace period
              }
              break;
            case 'memory_context_loaded':
              // Memory system loaded context - informational only
              logger.debug('[WebSocketService] Memory context loaded');
              break;
            
            // Worker delegation events
            case 'delegated':
              logger.debug(`[WebSocketService] Job delegated: ${data.jobId}`);
              handlers.onDelegated?.(data.jobId || '', data.message || 'Processing in background...', data.connectionId);
              break;
              
            case 'debug_state':
              logger.debug(`[WebSocketService] Debug state: job=${data.jobId}, turn=${data.turnCount}, chain=${data.chainCount}, elapsed=${data.elapsedMs}ms`);
              handlers.onDebugState?.({
                jobId: data.jobId || '',
                turnCount: data.turnCount || 0,
                chainCount: data.chainCount || 0,
                elapsedMs: data.elapsedMs || 0,
                timeoutMs: data.timeoutMs || 0,
              });
              break;
              
            case 'debug_chain':
              logger.debug(`[WebSocketService] Debug chain: reason=${data.reason}, chain #${data.chainCount}`);
              handlers.onDebugChain?.({
                jobId: data.jobId || '',
                chainCount: data.chainCount || 0,
                turnCount: data.turnCount || 0,
                reason: data.reason || 'unknown',
              });
              break;
              
            // Streaming content events from worker
            case 'text_delta':
              // Handle chunked text_delta messages
              if (data._chunked) {
                const { id, index, total, isLast } = data._chunked;
                
                // Initialize buffer if first chunk
                if (!this.chunkBuffers.has(id)) {
                  this.chunkBuffers.set(id, {
                    id,
                    total,
                    chunks: new Array(total).fill(''),
                    receivedCount: 0,
                    type: 'text_delta',
                  });
                }
                
                const buffer = this.chunkBuffers.get(id)!;
                buffer.chunks[index] = data.delta || '';
                buffer.receivedCount++;
                
                // Stream each chunk as it arrives for responsiveness
                handlers.onTextDelta?.(data.delta || '');
                
                // Clean up buffer when complete
                if (isLast || buffer.receivedCount === total) {
                  this.chunkBuffers.delete(id);
                  logger.debug(`[WebSocketService] Completed chunked text_delta: ${total} chunks`);
                }
              } else {
                handlers.onTextDelta?.(data.delta || '');
              }
              break;
              
            case 'thinking':
              handlers.onThinking?.(data.thought || '', data.toolName, data.icon, data.phase);
              break;
              
            case 'diagram_state':
              // Handle diagram state updates from plan-worker (diagram mode)
              logger.debug('[WebSocketService] diagram_state event received:', {
                deviceCount: data.devices?.length,
                wireCount: data.wires?.length,
                version: data.version,
                hasHandler: !!handlers.onDiagramState
              });
              handlers.onDiagramState?.({
                devices: data.devices || [],
                wires: data.wires || [],
                crossings: data.crossings || [],
                version: data.version || Date.now(),
              });
              break;
              
            case 'title_update':
              // Title updates are handled globally above, but may also come through job handlers
              logger.debug('[WebSocketService] Title update via job:', data.title);
              break;
              
            case 'operation_error':
              logger.error('[WebSocketService] Operation error:', data.message, data.operation);
              handlers.onOperationError?.(data.message || 'Operation failed', data.operation);
              break;
              
            case 'document_sync':
              logger.debug('[WebSocketService] Document sync received, s3VersionId:', data.s3VersionId, 'preAIVersionId:', data.preAIVersionId);
              handlers.onDocumentSync?.(data.document, data.version, data.s3VersionId, data.preAIVersionId);
              break;
              
            case 'document_sync_meta':
              logger.debug('[WebSocketService] Document sync meta received, version:', data.version, 's3VersionId:', data.s3VersionId, 'operationCount:', data.operationCount, 'sectionCount:', data.sectionCount);
              handlers.onDocumentSyncMeta?.(data.version, data.s3VersionId, data.operationCount, data.sectionCount);
              break;
              
            // Chunked document operation handling (for large operations > 100KB)
            case 'document_operation_chunk_start':
              if (data._chunked) {
                const { id, total, operationType } = data._chunked;
                logger.debug(`[WebSocketService] Starting chunked document_operation: ${total} chunks, type: ${operationType}`);
                this.chunkBuffers.set(id, {
                  id,
                  total,
                  chunks: new Array(total).fill(''),
                  receivedCount: 0,
                  type: 'document_operation',
                  metadata: { jobId: data.jobId, requestId: data.requestId },
                });
              }
              break;
              
            case 'document_operation_chunk':
              if (data._chunked) {
                const { id, index, content } = data._chunked;
                const buffer = this.chunkBuffers.get(id);
                if (buffer) {
                  buffer.chunks[index] = content;
                  buffer.receivedCount++;
                  logger.debug(`[WebSocketService] Received chunk ${index + 1}/${buffer.total} for document_operation`);
                }
              }
              break;
              
            case 'document_operation_chunk_end':
              if (data._chunked) {
                const { id } = data._chunked;
                const buffer = this.chunkBuffers.get(id);
                if (buffer) {
                  // Reassemble the operation
                  const operationStr = buffer.chunks.join('');
                  try {
                    const operation = JSON.parse(operationStr);
                    logger.debug(`[WebSocketService] Reassembled chunked document_operation: ${operationStr.length} bytes`);
                    
                    // Emit the reassembled operation
                    handlers.onDocumentOperation?.(operation, data.partial);
                    
                    // Also forward to global handler
                    if (typeof window.__handleDocumentOperation === 'function') {
                      window.__handleDocumentOperation(operation);
                    }
                  } catch (parseError) {
                    logger.error('[WebSocketService] Failed to parse reassembled document_operation:', parseError);
                    handlers.onOperationError?.('Failed to reassemble chunked operation', { type: 'unknown' } as DocumentOperation);
                  }
                  
                  // Clean up buffer
                  this.chunkBuffers.delete(id);
                }
              }
              break;
              
            // Develop feature autonomous loop events
            case 'develop_feature_progress':
              logger.debug(`[WebSocketService] Develop feature progress: phase=${data.phase}, iteration=${data.iteration}/${data.maxIterations}, message=${data.message}`);
              handlers.onDevelopProgress?.({
                jobId: data.jobId || '',
                phase: data.phase || 'unknown',
                iteration: data.iteration || 0,
                maxIterations: data.maxIterations || 10,
                message: data.message || '',
                success: data.success,
                errorCount: data.errorCount,
                errorType: data.errorType,
              });
              break;
              
            case 'develop_feature_complete':
              logger.debug(`[WebSocketService] Develop feature complete: success=${data.result?.success}, files=${data.result?.filesCreated?.length}`);
              handlers.onDevelopComplete?.({
                jobId: data.jobId || '',
                result: data.result || {},
                message: data.message || '',
              });
              // Clean up job handler
              if (data.jobId) {
                setTimeout(() => {
                  this.jobHandlers.delete(data.jobId!);
                }, 5000);
              }
              break;
              
            case 'develop_feature_paused':
              logger.debug(`[WebSocketService] Develop feature paused: reason=${data.reason}`);
              handlers.onDevelopPaused?.({
                jobId: data.jobId || '',
                reason: data.reason || 'unknown',
                message: data.message || '',
                errors: data.errors || [],
              });
              break;
              
            // Develop mode AI text streaming (bootstrap + expand-scope turns)
            case 'text':
              handlers.onTextDelta?.(data.text || '');
              break;

            // Micro-unit events (requirements-based micro-coding)
            case 'micro_unit_progress':
              logger.debug(`[WebSocketService] Micro-unit progress: reqId=${data.reqId}, phase=${data.phase}, message=${data.message}`);
              handlers.onMicroUnitProgress?.(data);
              break;
              
            case 'micro_unit_complete':
              logger.debug(`[WebSocketService] Micro-unit complete: reqId=${data.reqId}`);
              handlers.onMicroUnitComplete?.(data);
              break;
              
            case 'micro_unit_paused':
              logger.debug(`[WebSocketService] Micro-unit paused: reqId=${data.reqId}, reason=${data.reason}`);
              handlers.onMicroUnitPaused?.(data);
              break;
              
            default:
              logger.warn('[WebSocketService] Unknown message type:', data.type);
          }
          
          // Clean up job handlers on terminal events
          if (data.jobId && (data.type === 'done' || data.type === 'execution_done' || data.type === 'plan_done' || data.type === 'error' || data.type === 'plan_error')) {
            setTimeout(() => {
              this.jobHandlers.delete(data.jobId!);
              logger.debug(`[WebSocketService] Cleaned up job handler: ${data.jobId}`);
            }, 5000);
          }
        } catch (error) {
          logger.error('[WebSocketService] Failed to parse message:', event.data, error);
        }
      };
    });
    
    return this.connectionPromise;
  }
  
  /**
   * Send a message over the persistent WebSocket connection
   * @param body Message payload
   * @param handlers Stream event handlers
   * @param token Fresh auth token (optional - will use cached if not provided)
   */
  async sendMessage(body: Record<string, unknown>, handlers: StreamHandlers, token?: string): Promise<void> {
    const requestId = crypto.randomUUID();
    
    // Update token if provided (to handle token refresh)
    if (token) {
      this.token = token;
    }
    
    return new Promise<void>((resolve, reject) => {
      // If not connected, queue the message
      if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
        logger.debug('[WebSocketService] Connection not ready, queueing message');
        this.messageQueue.push({ body, handlers, requestId, resolve, reject });
        
        // Try to connect if not already connecting
        if (!this.connectionPromise && this.wsUrl && this.token) {
          this.connect(this.wsUrl, this.token).catch(err => {
            logger.error('[WebSocketService] Failed to connect:', err);
          });
        }
        
        return;
      }
      
      // Register handlers for this request
      this.currentRequestId = requestId;  // Track as active
      this.messageHandlers.set(requestId, {
        ...handlers,
        onDone: (message, edits, tokenUsage, truncated, stopReason, resolvedModel) => {
          handlers.onDone?.(message, edits, tokenUsage, truncated, stopReason, resolvedModel);
          this.currentRequestId = null;  // Clear active request
          resolve();
        },
        onError: (message) => {
          handlers.onError?.(message);
          this.currentRequestId = null;  // Clear active request
          reject(new Error(message));
        },
      });
      
      // Send message with requestId
      try {
        this.socket.send(JSON.stringify({
          action: 'start_chat',
          requestId,
          ...body,
          authToken: this.token,
        }));
        
        this.resetIdleTimeout();
      } catch (error: unknown) {
        this.messageHandlers.delete(requestId);
        this.currentRequestId = null;
        reject(error);
      }
    });
  }
  
  /**
   * Process queued messages after connection established
   */
  private processMessageQueue() {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      return;
    }
    
    logger.debug(`[WebSocketService] Processing ${this.messageQueue.length} queued messages`);
    
    const queue = [...this.messageQueue];
    this.messageQueue = [];
    
    queue.forEach(msg => {
      // Register handlers
      this.messageHandlers.set(msg.requestId, {
        ...msg.handlers,
        onDone: (message, edits, tokenUsage, truncated, stopReason, resolvedModel) => {
          msg.handlers.onDone?.(message, edits, tokenUsage, truncated, stopReason, resolvedModel);
          msg.resolve();
        },
        onError: (message) => {
          msg.handlers.onError?.(message);
          msg.reject(new Error(message));
        },
      });
      
      // Send message
      try {
        this.socket!.send(JSON.stringify({
          action: 'start_chat',
          requestId: msg.requestId,
          ...msg.body,
          authToken: this.token,
        }));
      } catch (error: unknown) {
        this.messageHandlers.delete(msg.requestId);
        msg.reject(error);
      }
    });
  }
  
  /**
   * Register handlers for a specific job (used for worker delegation)
   * When delegated jobs stream results, they include jobId for routing
   */
  registerJobHandlers(jobId: string, handlers: StreamHandlers): void {
    logger.debug(`[WebSocketService] Registered handlers for job ${jobId}`);
    this.jobHandlers.set(jobId, handlers);
  }
  
  /**
   * Unregister handlers for a job (when job completes or is cancelled)
   */
  unregisterJobHandlers(jobId: string): void {
    logger.debug(`[WebSocketService] Unregistered handlers for job ${jobId}`);
    this.jobHandlers.delete(jobId);
  }
  
  /**
   * Get the WebSocket connection ID (assigned by API Gateway)
   * Used for worker delegation - workers need this to stream results back
   */
  getConnectionId(): string | null {
    return this.connectionId;
  }
  
  /**
   * Schedule automatic reconnection with exponential backoff
   */
  private scheduleReconnect() {
    if (this.reconnectTimeout) {
      return;
    }
    
    this.reconnectAttempts++;
    
    // Exponential backoff: 1s, 2s, 4s, 8s, 16s (capped at RECONNECT_MAX_DELAY_MS)
    const delay = Math.min(
      this.RECONNECT_BASE_DELAY_MS * Math.pow(2, this.reconnectAttempts - 1),
      this.RECONNECT_MAX_DELAY_MS
    );
    
    logger.debug(`[WebSocketService] Reconnecting in ${delay}ms... attempt ${this.reconnectAttempts}/${this.MAX_RECONNECT_ATTEMPTS}`);
    
    this.reconnectTimeout = setTimeout(() => {
      this.reconnectTimeout = null;
      
      if (this.wsUrl && this.token) {
        this.connect(this.wsUrl, this.token).catch(err => {
          logger.error('[WebSocketService] Reconnection failed:', err);
        });
      }
    }, delay);
  }
  
  /**
   * Reset idle timeout (call on activity)
   */
  private resetIdleTimeout() {
    if (this.idleTimeout) {
      clearTimeout(this.idleTimeout);
    }
    
    this.idleTimeout = setTimeout(() => {
      logger.debug('[WebSocketService] Idle timeout, closing connection');
      this.close(false); // Don't reconnect on idle timeout
    }, this.IDLE_TIMEOUT_MS);
  }
  
  /**
   * Start keepalive ping interval
   * Sends periodic pings to prevent API Gateway from closing idle connections
   */
  private startKeepalive() {
    this.stopKeepalive(); // Clear any existing interval
    
    this.keepaliveInterval = setInterval(() => {
      if (this.socket?.readyState === WebSocket.OPEN) {
        try {
          logger.debug('[WebSocketService] Sending keepalive ping');
          this.socket.send(JSON.stringify({ action: 'ping' }));
        } catch (error) {
          logger.warn('[WebSocketService] Failed to send keepalive ping:', error);
        }
      }
    }, this.KEEPALIVE_INTERVAL_MS);
    
    logger.debug(`[WebSocketService] Keepalive started (every ${this.KEEPALIVE_INTERVAL_MS / 1000}s)`);
  }
  
  /**
   * Stop keepalive ping interval
   */
  private stopKeepalive() {
    if (this.keepaliveInterval) {
      clearInterval(this.keepaliveInterval);
      this.keepaliveInterval = null;
      logger.debug('[WebSocketService] Keepalive stopped');
    }
  }
  
  /**
   * Reset reconnection state — allows a fresh connection cycle.
   * Called by the React layer when it wants to force a new connection
   * (e.g., after all internal reconnect attempts have been exhausted).
   */
  resetReconnectionState(): void {
    this.reconnectAttempts = 0;
    this.shouldReconnect = true;
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
    this.connectionPromise = null;
    logger.debug('[WebSocketService] Reconnection state reset — ready for fresh connection cycle');
  }
  
  /**
   * Add listener for connection state changes
   */
  addConnectionListener(listener: (connected: boolean) => void): () => void {
    this.connectionListeners.add(listener);
    
    // Immediately notify of current state
    listener(this.socket?.readyState === WebSocket.OPEN);
    
    // Return unsubscribe function
    return () => {
      this.connectionListeners.delete(listener);
    };
  }
  
  /**
   * Notify all connection listeners of state change
   */
  private notifyConnectionListeners(connected: boolean) {
    this.connectionListeners.forEach(listener => {
      try {
        listener(connected);
      } catch (error) {
        logger.error('[WebSocketService] Listener error:', error);
      }
    });
  }
  
  /**
   * Check if currently connected
   */
  isConnected(): boolean {
    return this.socket?.readyState === WebSocket.OPEN;
  }
  
  /**
   * Ensure WebSocket is connected and has a valid connectionId.
   * Reconnects if needed. Used by Agent mode before delegating to workers.
   * @param wsUrl WebSocket URL (required if not already connected)
   * @param token Auth token (required if not already connected)
   * @param timeoutMs Maximum time to wait for connectionId (default 10s)
   * @returns connectionId or throws if unable to connect
   */
  async ensureConnectedWithId(wsUrl: string, token: string, timeoutMs = 10000): Promise<string> {
    const MAX_RETRIES = 2;
    
    for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
      try {
        // If already connected with a valid connectionId, return it
        if (this.isConnected() && this.connectionId) {
          logger.debug('[WebSocketService] Already connected with connectionId:', this.connectionId);
          return this.connectionId;
        }
        
        // Need to (re)connect
        logger.debug(`[WebSocketService] Connecting to get connectionId (attempt ${attempt + 1}/${MAX_RETRIES + 1})...`);
        
        // Clear stale state before reconnecting
        if (attempt > 0) {
          logger.debug('[WebSocketService] Retrying - cleaning up previous connection state');
          this.cleanupHandlers();
          this.socket = null;
          // Small delay before retry to allow socket to fully close
          await new Promise(resolve => setTimeout(resolve, 500));
        }
        
        // Clear stale connectionId
        this.connectionId = null;
        
        // Connect (or reconnect)
        await this.connect(wsUrl, token);
        
        // Verify socket is actually open
        if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
          logger.warn('[WebSocketService] Socket not in OPEN state after connect');
          continue; // Retry
        }
        
        // Explicitly request connectionId again in case the onopen request was missed
        if (!this.connectionId) {
          logger.debug('[WebSocketService] Sending additional get_connection_id request');
          try {
            this.socket.send(JSON.stringify({ action: 'get_connection_id' }));
          } catch (sendError) {
            logger.warn('[WebSocketService] Failed to send get_connection_id:', sendError);
            continue; // Retry
          }
        }
        
        // Wait for connectionId to be received
        const startTime = Date.now();
        while (!this.connectionId && Date.now() - startTime < timeoutMs) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }
        
        if (this.connectionId) {
          logger.debug('[WebSocketService] Got connectionId:', this.connectionId);
          return this.connectionId;
        }
        
        logger.warn(`[WebSocketService] Timeout waiting for connectionId (attempt ${attempt + 1})`);
      } catch (error) {
        logger.error(`[WebSocketService] Connection attempt ${attempt + 1} failed:`, error);
        if (attempt === MAX_RETRIES) {
          throw error;
        }
      }
    }
    
    throw new Error('Failed to establish WebSocket connection: Timed out waiting for WebSocket connectionId after retries');
  }
  
  /**
   * Close the WebSocket connection
   * @param shouldReconnect Whether to attempt reconnection
   */
  close(shouldReconnect = false) {
    this.shouldReconnect = shouldReconnect;
    
    if (this.socket) {
      this.socket.close();
    }
    
    if (!shouldReconnect) {
      this.cleanupFull();
    }
  }
  
  /**
   * Cancel current in-progress request (CRITICAL FIX: Stop backend execution)
   */
  cancelCurrentRequest(): void {
    if (!this.currentRequestId) {
      logger.debug('[WebSocketService] No active request to cancel');
      return;
    }
    
    const requestId = this.currentRequestId;
    logger.debug(`[WebSocketService] Cancelling request ${requestId}`);
    
    // Send cancel message to backend (best effort)
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      try {
        this.socket.send(JSON.stringify({
          action: 'cancel_request',
          requestId,
        }));
      } catch (error) {
        logger.error('[WebSocketService] Failed to send cancel:', error);
      }
    }
    
    // CRITICAL: Close connection to force backend to stop
    // Backend Lambda will fail when trying to send messages to closed connection
    // This prevents multiple concurrent executions
    this.shouldReconnect = false;
    if (this.socket) {
      this.socket.close();
    }
    this.cleanupFull();
    
    // Clean up frontend handlers immediately
    this.messageHandlers.delete(requestId);
    this.currentRequestId = null;
    
    logger.debug(`[WebSocketService] Request ${requestId} cancelled and connection closed`);
  }
  
  /**
   * Clean up handlers and notify of errors — but preserve wsUrl/token/connectionId
   * so the React layer can trigger a fresh connection cycle.
   * Called when reconnect attempts are exhausted but shouldReconnect is still true.
   */
  private cleanupHandlers() {
    this.socket = null;
    this.connectionPromise = null;
    this.connectionId = null;
    
    // Notify pending message handlers of the disconnect
    this.messageHandlers.forEach((handlers, _requestId) => {
      handlers.onError?.('Connection lost — reconnecting...');
    });
    this.messageHandlers.clear();
    
    // DON'T clear jobHandlers — worker may still be processing, 
    // and we need handlers intact for when we re-register after reconnect
    
    this.chunkBuffers.clear();
    
    // Stop keepalive pings
    this.stopKeepalive();
    
    if (this.idleTimeout) {
      clearTimeout(this.idleTimeout);
      this.idleTimeout = null;
    }
    
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
  }
  
  /**
   * Full cleanup — wipes everything including credentials.
   * Called on intentional disconnects (idle timeout, manual close).
   */
  private cleanupFull() {
    this.socket = null;
    this.connectionPromise = null;
    this.wsUrl = null;
    this.token = null;
    this.connectionId = null;
    this.messageHandlers.clear();
    this.jobHandlers.clear();
    this.chunkBuffers.clear();
    this.reconnectAttempts = 0;
    
    // Reject queued messages
    this.messageQueue.forEach(msg => {
      msg.reject(new Error('Connection closed'));
    });
    this.messageQueue = [];
    
    // Stop keepalive pings
    this.stopKeepalive();
    
    if (this.idleTimeout) {
      clearTimeout(this.idleTimeout);
      this.idleTimeout = null;
    }
    
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
  }
  
  /**
   * Get active job handler IDs — used for re-registering after reconnect
   */
  getActiveJobIds(): string[] {
    return Array.from(this.jobHandlers.keys());
  }

  // =================================================================
  // MICRO-UNIT SUBSCRIPTIONS
  // =================================================================

  /**
   * Subscribe to project-level micro-unit status updates.
   * Called when the Requirements Index component mounts.
   */
  subscribeMicroProject(projectId: string): void {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      logger.warn('[WebSocketService] Cannot subscribe: not connected');
      return;
    }
    this.socket.send(JSON.stringify({
      action: 'subscribe_micro_project',
      projectId,
    }));
    logger.debug('[WebSocketService] subscribe_micro_project', { projectId });
  }

  /**
   * Unsubscribe from project-level micro-unit status updates.
   * Called when the Requirements Index component unmounts.
   */
  unsubscribeMicroProject(projectId: string): void {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      return;
    }
    this.socket.send(JSON.stringify({
      action: 'unsubscribe_micro_project',
      projectId,
    }));
    logger.debug('[WebSocketService] unsubscribe_micro_project', { projectId });
  }

  /**
   * Subscribe to unit-level micro-unit content updates.
   * Called when a Micro-Unit Detail tab is opened.
   */
  subscribeMicroUnit(projectId: string, reqId: string): void {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      logger.warn('[WebSocketService] Cannot subscribe: not connected');
      return;
    }
    this.socket.send(JSON.stringify({
      action: 'subscribe_micro_unit',
      projectId,
      reqId,
    }));
    logger.debug('[WebSocketService] subscribe_micro_unit', { projectId, reqId });
  }

  /**
   * Unsubscribe from unit-level micro-unit content updates.
   * Called when a Micro-Unit Detail tab is closed.
   */
  unsubscribeMicroUnit(projectId: string, reqId: string): void {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      return;
    }
    this.socket.send(JSON.stringify({
      action: 'unsubscribe_micro_unit',
      projectId,
      reqId,
    }));
    logger.debug('[WebSocketService] unsubscribe_micro_unit', { projectId, reqId });
  }
}

// Export singleton instance
export const websocketService = new WebSocketService();
